import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import { useAppDispatch, useAppSelector } from "../../../store/reducers";
import { trackEvent } from "../../../utils/analytics";
import { focusElement } from "../../../utils/keyboardFocus";
import { Review } from "../Review";
import { defaultState } from "../defaults";
import { clearFilters } from "../reducers";

type IProps = Immutable<{
    showSideDate: boolean;
    enablePDPLinks: boolean;
    showRecommended: boolean;
    showVerboseUserInfo: boolean;
    starHasStroke?: boolean;
}>;

export const ReviewList = (props: IProps): React.ReactNode => {
    const listElem = React.useRef<HTMLDivElement>(null);

    // Select state from Redux
    const reviews = useAppSelector((rootState) => {
        const state = rootState.reviews || defaultState;
        return state.data.reviews;
    });
    const page = useAppSelector((rootState) => {
        const state = rootState.reviews || defaultState;
        return state.ui.page;
    });

    // Get dispatch
    const dispatch = useAppDispatch();

    // Track previous values to detect changes
    const prevPageRef = React.useRef(page);
    const prevReviewsLengthRef = React.useRef(reviews.length);

    React.useEffect(() => {
        // If we just loaded new reviews, focus on the first new one
        if (page > prevPageRef.current) {
            if (listElem.current) {
                const reviewElems =
                    listElem.current.querySelectorAll<HTMLElement>(
                        `.customer-review__review-item`,
                    );
                const firstNewReview =
                    reviewElems[prevReviewsLengthRef.current];
                if (firstNewReview) {
                    focusElement(firstNewReview);
                }
            }
        }
        prevPageRef.current = page;
        prevReviewsLengthRef.current = reviews.length;
    }, [page, reviews.length]);

    // Track review count
    React.useEffect(() => {
        trackEvent("Reviews", "reviewCount", "Review Count", {
            nonInteraction: true,
            numReviews: reviews.length,
        });
    }, [reviews.length]);

    if (reviews.length === 0) {
        trackEvent("Reviews", "noReviewsFound", undefined, {
            nonInteraction: true,
        });
        return (
            <div className="customer-review__no-reviews">
                <div className="customer-review__no-reviews-copy">
                    {t`Sorry, we could not find any reviews within your criteria.`}
                </div>
                <div
                    className="customer-review__no-reviews-cta"
                    onClick={() => dispatch(clearFilters())}
                >
                    {t`Clear filters and try again?`}
                </div>
            </div>
        );
    }
    return (
        <div ref={listElem}>
            {reviews.map((data) => (
                <Review
                    key={`${data.product_id}-${data.id}`}
                    review={data}
                    showSideDate={props.showSideDate}
                    enablePDPLinks={props.enablePDPLinks}
                    showRecommended={props.showRecommended}
                    showVerboseUserInfo={props.showVerboseUserInfo}
                    starHasStroke={props.starHasStroke}
                />
            ))}
        </div>
    );
};
